import React from 'react';
import { Container, Grid, Typography, Theme, Box } from '@mui/material';
import { ProfileAvatar, Rating } from '@care/react-component-lib';
import useMediaQuery from '@mui/material/useMediaQuery';
import Link from 'next/link';
import { getUserInitials } from '@/utils/getUserInitials';
import { SxClassProps } from '@/types/common';

interface Members {
  displayName: string;
  imageURL: string;
  address: {
    city: string;
  };
}
interface NearByCaregivers {
  avgReviewRating: number;
  member: Members;
  profileURL: string;
}
const classes: SxClassProps = {
  similarProfileContainer: {
    marginTop: 4,
  },
  gridContainer: (theme) => ({
    maxWidth: '328px',
    margin: 'auto',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      maxWidth: '960px',
      textAlign: 'left',
    },
  }),
  similarProfileInfo: {
    display: 'block',
  },
  profileSection: (theme) => ({
    marginTop: 3,
    cursor: 'pointer',
    display: 'block',
    '& a': {
      textDecoration: 'none',
      color: theme.palette.care.grey[900],
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: '100px',
      textAlign: 'center',
    },
  }),
  similarProfileImg: {
    display: 'flex',
    justifyContent: 'center',
  },
  profileName: (theme) => ({
    marginTop: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(1.5),
    },
  }),
};

const SimilarProfiles = ({ profiles }: { profiles: NearByCaregivers[] }) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  return (
    <Container maxWidth="md" sx={classes.similarProfileContainer}>
      <Typography variant={isMobileOrSmaller ? 'h2' : 'h1'}>Similar caregivers nearby</Typography>
      <Grid container sx={classes.gridContainer}>
        {profiles.map((profile: NearByCaregivers, index) => {
          return (
            <React.Fragment key={profile.member.displayName}>
              {profile.profileURL && (
                <Grid
                  item
                  md={2}
                  xs={6}
                  display={profiles.length === index + 1 && isMobileOrSmaller ? 'none' : 'block'}>
                  <Box sx={classes.profileSection} data-testid="similar-profiles">
                    {/* Link component is enclosing the content with <p> tag. Using <a> tag as an workaround. */}
                    <Link href={profile.profileURL}>
                      <a href={profile.profileURL}>
                        <Box sx={classes.similarProfileImg}>
                          <ProfileAvatar alt="profile" src={profile.member.imageURL} size="xLarge">
                            {getUserInitials(profile.member.displayName)}
                          </ProfileAvatar>
                        </Box>
                        <Box sx={classes.similarProfileInfo}>
                          <Typography variant="h4" sx={classes.profileName}>
                            {profile.member.displayName}
                          </Typography>

                          <Box>
                            <Rating
                              name="size-large"
                              size="small"
                              defaultValue={profile.avgReviewRating}
                              readOnly
                            />
                          </Box>

                          <Typography variant="info1" color="secondary">
                            {profile.member.address.city}
                          </Typography>
                        </Box>
                      </a>
                    </Link>
                  </Box>
                </Grid>
              )}
            </React.Fragment>
          );
        })}
      </Grid>
    </Container>
  );
};

export default SimilarProfiles;
